import { parseAcquisitionSource } from '@houzy/schemas/tracking';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useNavigate } from '@tanstack/react-router';
import { useState, type ReactElement, type ReactNode, type SubmitEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { TextLink } from '../../../components/TextLink';
import { isSupportedLanguage } from '../../../i18n';
import { apiErrorCode, apiErrorMessageKey } from '../../../lib/api';
import { useRequestOnboardingLinkMutation } from '../../../lib/auth';
import { houzyContact } from '../../../lib/houzyContact';
import { useTrackEvent } from '../../../lib/trackingEvents';
import { isValidEmail } from '../../../lib/validators';
import { TermsConsent } from '../../auth/components/TermsConsent';

const ACCOUNT_EXISTS = 'ONBOARDING_COCKPIT_ACCOUNT_ALREADY_EXISTS';
const PARTNER_CLAIMED = 'ONBOARDING_PARTNER_ALREADY_HAS_COCKPIT_ACCOUNT';
const ACCOUNT_UNAVAILABLE = 'ONBOARDING_COCKPIT_ACCOUNT_UNAVAILABLE';

function registrationErrorBody(errorCode: string | undefined): ReactNode {
    if (errorCode === ACCOUNT_EXISTS) {
        return (
            <Trans
                i18nKey="ONBOARDING.REGISTER.ERRORS.ACCOUNT_EXISTS_BODY"
                components={{ loginLink: <TextLink to="/login" color="primary" /> }}
            />
        );
    }
    if (errorCode === PARTNER_CLAIMED || errorCode === ACCOUNT_UNAVAILABLE) {
        const bodyKey =
            errorCode === PARTNER_CLAIMED
                ? 'ONBOARDING.REGISTER.ERRORS.PARTNER_CLAIMED_BODY'
                : 'ONBOARDING.REGISTER.ERRORS.ACCOUNT_UNAVAILABLE_BODY';
        return (
            <Trans
                i18nKey={bodyKey}
                values={{ phoneNumber: houzyContact.partnerManagementPhoneNumber }}
                components={{
                    phoneLink: (
                        <Link href={houzyContact.partnerManagementPhoneHref} color="primary" underline="hover" />
                    ),
                    supportLink: (
                        <Link href={`mailto:${houzyContact.supportEmail}`} color="primary" underline="hover" />
                    ),
                }}
            />
        );
    }
    return undefined;
}

export function RegisterCompanyPage(): ReactElement {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const requestLink = useRequestOnboardingLinkMutation();
    const track = useTrackEvent();
    const [email, setEmail] = useState('');
    const [agreedToTerms, setAgreedToTerms] = useState(false);

    const canSubmit = isValidEmail(email) && agreedToTerms;
    const errorCode = apiErrorCode(requestLink.error);
    const errorTitle = requestLink.error
        ? t(apiErrorMessageKey(requestLink.error, 'ONBOARDING.REGISTER.ERRORS.REQUEST_FAILED'))
        : undefined;
    const errorBody = registrationErrorBody(errorCode);

    const handleSubmit = (event: SubmitEvent<HTMLFormElement>): void => {
        event.preventDefault();
        if (!canSubmit || requestLink.isPending) {
            return;
        }
        const acquisitionSource = parseAcquisitionSource(window.location.href);
        requestLink.mutate(
            {
                email,
                tosAccepted: true,
                acquisitionSource,
                ...(isSupportedLanguage(i18n.language) ? { language: i18n.language } : {}),
            },
            {
                onSuccess: () => {
                    track('Onboarding Link Requested', {});
                    void navigate({ to: '/register-company/check-email', search: { email } });
                },
            },
        );
    };

    return (
        <Stack spacing={4}>
            <Stack spacing={1}>
                <Typography variant="h5" component="h1">
                    {t('ONBOARDING.REGISTER.TITLE')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {t('ONBOARDING.REGISTER.DESCRIPTION')}
                </Typography>
            </Stack>

            <Stack component="form" spacing={2} noValidate onSubmit={handleSubmit}>
                {errorTitle && (
                    <Alert severity="error">
                        {errorBody === undefined ? (
                            errorTitle
                        ) : (
                            <>
                                <AlertTitle>{errorTitle}</AlertTitle>
                                {errorBody}
                            </>
                        )}
                    </Alert>
                )}
                <TextField
                    label={t('ONBOARDING.REGISTER.EMAIL')}
                    type="email"
                    fullWidth
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                />
                <TermsConsent checked={agreedToTerms} onChange={setAgreedToTerms} />
                <Button type="submit" variant="contained" fullWidth disabled={!canSubmit || requestLink.isPending}>
                    {t('ONBOARDING.REGISTER.SUBMIT')}
                </Button>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                    <Trans
                        i18nKey="ONBOARDING.REGISTER.HAVE_ACCOUNT"
                        components={{ loginLink: <TextLink to="/login" color="primary" /> }}
                    />
                </Typography>
            </Stack>
        </Stack>
    );
}
